/// <amd-module name="Wit/Common/WorkItemDialogs/Components/WorkItemFormDialog" />

import * as React from "react";
import { TfsComponent } from "Tfs/Platform/TfsComponent";
import { Components, css, IVssComponentContext, WrappedComponent } from "VSS/Platform/Layout";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { Button } from "VSSUI/Button";
import { ContentSize } from "VSSUI/Callout";
import { CustomDialog } from "VSSUI/Dialog";
import { Spinner } from "VSSUI/Spinner";
import { IWorkItemFormDialogProps } from "Wit/Common/WorkItemDialogs/Components/WorkItemFormDialog.Props";
import * as Resources from "Wit/Common/WorkItemDialogs/Resources";
import { IWorkItemFormPropsWithId, IWorkItemFormPropsWithWorkItem, IWorkItemFormViewProps } from "Wit/WorkItemForm/Contracts/Contracts";
import { ActiveWorkItemService } from "Wit/WorkItemFormExtension/WorkItemFormExtensionService";
import { IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { IWorkItemModelService } from "Wit/WorkItemModel/Services/IWorkItemModelService";

const WifDialogFullScreenSettingKey = "WorkItemForm/WifDialog/FullScreen";

export interface IWorkItemFormDialogState {
    fullscreen: boolean;
    defaultFocusableElementAriaLabel: string;
}

export class WorkItemFormDialog extends TfsComponent<IWorkItemFormDialogProps, IWorkItemFormDialogState> {
    private _activeWorkItemService: ActiveWorkItemService;
    private _settingsService: ISettingsService;
    private _previousActiveId?: number;
    private _currentWorkItem: Promise<IWorkItem> | IWorkItem;

    public constructor(props: IWorkItemFormDialogProps, context: IVssComponentContext) {
        super(props, context);

        this._settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");

        this.state = {
            fullscreen: this._settingsService.getEntry<boolean>(WifDialogFullScreenSettingKey, SettingsUserScope.Me) ?? false,
            defaultFocusableElementAriaLabel: ""
        };

        this._setPreviousId();
        this._setCurrentWorkItem();
    }

    public componentDidMount(): void {
        this._setAriaLabelForDefaultFocusableElement();
    }

    private _setPreviousId = async () => {
        this._activeWorkItemService = this.context.pageContext.getService<ActiveWorkItemService>("ms.vss-work-web.active-work-item-service");

        if (!!this._activeWorkItemService.activeWorkItem) {
            this._previousActiveId = await this._activeWorkItemService.activeWorkItem.id;
        }
    };

    public render() {
        return (
            <CustomDialog
                calloutContentClassName={css("work-item-form-dialog", this.state.fullscreen && "fullscreen")}
                contentSize={ContentSize.Auto}
                escDismiss
                lightDismiss={false}
                resizable={!this.state.fullscreen}
                onDismiss={this._onDismiss}
                defaultFocusableElementAriaLabel={this.state.defaultFocusableElementAriaLabel}
                selectInputTextOnFocus={true}
            >
                <WrappedWorkItemFormView
                    {...this.props}
                    onDismiss={this._onInternalDismiss}
                    onRegisterTryShowSaveDialog={this._onRegisterTryShowSaveDialog}
                    onToggleFullscreen={this.onToggleFullscreen}
                />
            </CustomDialog>
        );
    }

    private tryShowSaveDialog?: () => Promise<{ canDismiss: true }>;
    // TODO This should be refactored as a service, but for now we'll just pass it through the props callback
    // Unfortunately, "unsaved changes dialog" currently depends on WorkItemFormView.onSave
    // which in turn depends on LegacyWorkItem and onLegacySave prop so this should refactored when we remove these legacy adapters
    // Should be fixed as part of: https://dev.azure.com/mseng/AzureDevOps/_workitems/edit/2013537
    private _onRegisterTryShowSaveDialog = (tryClose: () => Promise<{ canDismiss: true }>) => {
        this.tryShowSaveDialog = tryClose;
    };

    private _onDismiss = async () => {
        if (this.tryShowSaveDialog) {
            const closeResult = await this.tryShowSaveDialog();
            if (!closeResult.canDismiss) {
                return;
            }
        }

        await this._onInternalDismiss();
    };

    private _onInternalDismiss = async () => {
        this.props.onDismiss();
        if (this._activeWorkItemService && this._previousActiveId !== undefined) {
            const workItemModelService = this.context.pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
            const workItem = await workItemModelService.getWorkItem(this._previousActiveId);
            this._activeWorkItemService.setActiveWorkItem(workItem);
        }
    };

    private onToggleFullscreen = () => {
        const currentValue = this.state.fullscreen;

        this.setState({ fullscreen: !currentValue });

        this._settingsService.setEntries({ [WifDialogFullScreenSettingKey]: !currentValue }, SettingsUserScope.Me);
    };

    private async _setAriaLabelForDefaultFocusableElement(): Promise<void> {
        if (!this._currentWorkItem) {
            return;
        }

        const workItem = await this._currentWorkItem;
        if (!workItem) {
            return;
        }

        this.setState({
            defaultFocusableElementAriaLabel: `${workItem.getCaption()} ${workItem.getTitle()}`
        });
    }

    private _setCurrentWorkItem(): void {
        const { workItem } = this.props as IWorkItemFormPropsWithWorkItem;
        const { workItemId } = this.props as IWorkItemFormPropsWithId;
        const workItemModelService = this.context.pageContext.getService<IWorkItemModelService>("IWorkItemModelService");

        if (workItem) {
            this._currentWorkItem = workItem;
        } else if (workItemId) {
            this._currentWorkItem = workItemModelService.getWorkItem(workItemId);
        }
    }
}

/**
 * Load a WorkItemFormView as a WrappedComponent.
 * WrappedComponent ensures we get all of the WorkItemFormView's dependencies from the contribution
 * graph, and wrapping it in this function lets us get some type safety on the props we pass in.
 */
const WrappedWorkItemFormView = (props: IWorkItemFormViewProps) => (
    <WrappedComponent
        {...props}
        dependencies={["ms.vss-work-web.work-item-form-dependencies"]}
        loadingComponent={() => (
            <div className="work-item-form-loading-placeholder flex-column justify-center">
                <div className="full-width flex-row justify-end">
                    <Button
                        ariaLabel={Resources.CloseLabel}
                        iconProps={{ iconName: "ChromeClose" }}
                        onClick={() => props.onDismiss!()}
                        subtle
                        tooltipProps={{ text: Resources.CloseLabel }}
                    />
                </div>
                <div className="flex-grow flex-row justify-center">
                    <Spinner />
                </div>
            </div>
        )}
        wrappedType="work-item-form-view-component"
    />
);

Components.add("work-item-form-dialog", WorkItemFormDialog);
